<template>
  <v-row style="padding-top:12px">
    <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
      <v-toolbar>
        <v-toolbar-title>Term Manager</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-menu offset-y bottom>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" small text>
              <v-icon small left>fas fa-caret-down</v-icon>
              {{ range === 'future' ? 'Future Terms Only' : (range === 'past' ? 'Past Terms Only' : 'All Terms') }}
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item :disabled="range === 'future'" @click="range = 'future'">
              <v-list-item-title>Future Terms Only</v-list-item-title>
            </v-list-item>
            <v-list-item :disabled="range === 'past'" @click="range = 'past'">
              <v-list-item-title>Past Terms Only</v-list-item-title>
            </v-list-item>
            <v-list-item :disabled="range === 'all'" @click="range = 'all'">
              <v-list-item-title>All Terms</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-dialog v-model="infoModal" width="600">
          <template v-slot:activator="{ on: dialogOn }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltipOn }">
                <v-btn v-on="{ ...dialogOn, ...tooltipOn }" small color="primary" style="margin-left:1em">
                  <v-icon small left>fas fa-question-circle</v-icon>
                  Term Dates
                </v-btn>
              </template>
              <span>Details about the term dates</span>
            </v-tooltip>
          </template>
          <v-card>
            <v-card-title>Details about Term Dates</v-card-title>
            <v-card-text>
              <p>The dates will be used to determine 2 things. First, the open date will be the date when students in that term will be able to access Scots Connect.</p>
              <p>Second, the dates will be used to determine when the students' information will be loaded into Scots Connect. Student data will load into the system starting one week prior to the open date, and will continue to be loaded until 2 days after the term start date (to catch any really late arrivals).</p>
              <p>Students will have access to Scots Connect and all of the content for their entrance term for the entire time they are a student at Covenant. It will be locked to the term in which they started, but they will be able to return to the system to review any of the information that was entered for their starting term at Covenant.</p>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="infoModal = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-if="isAdmin" v-model="addTermActive" width="400">
          <template v-slot:activator="{ on: dialogOn }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltipOn }">
                <v-btn v-on="{ ...dialogOn, ...tooltipOn }" small color="success" style="margin-left:1em">
                  <v-icon left>fal fa-plus</v-icon>
                  Add Term
                </v-btn>
              </template>
              <span>Add new term</span>
            </v-tooltip>
          </template>
          <v-card>
            <v-card-title>Add New Term</v-card-title>
            <v-card-text v-if="!addingTerm">
              <v-form v-model="formModel" ref="form">
                <v-select v-model="termType" :rules="required" :items="[{ text: 'Student Term', value: 'Student' },{ text: 'Family Edition', value: 'Family' }]" label="Term Type" outlined></v-select>
                <v-combobox v-if="termType === 'Student'" v-model="newTerm" :rules="required" :items="termSearchResults" label="Term Search" search-input.sync="termSearch" item-text="label" item-value="term" return-object outlined></v-combobox>
                <v-text-field v-else-if="termType === 'Family'" v-model="familyTerm" :rules="required" label="Family Edition Term Label" outlined></v-text-field>
                <date-picker v-if="termType === 'Family'" v-model="newTermStart" :rules="required" label="Connected Term Start Date" outlined></date-picker>
                <date-picker v-model="newTermOpen" :rules="required" label="Scots Connect Open Date" outlined></date-picker>
                <v-select v-model="copyTerm" :items="copyTermOptions" label="Copy from term" item-text="label" item-value="term" outlined clearable></v-select>
              </v-form>
            </v-card-text>
            <v-card-text v-else>
              <p>Adding the term content. Progress:</p>
              <v-progress-linear v-if="addingTerm && copyTerm" v-model="addTermProgress" height="25">
                <strong>{{ Math.ceil(addTermProgress) }}%</strong>
              </v-progress-linear>
            </v-card-text>
            <v-card-actions v-if="addingTerm">
              <span>Adding the term and copying the content. This can take a few minutes.</span>
            </v-card-actions>
            <v-card-actions v-else>
              <v-btn text color="success" @click="addTerm">Add New Term</v-btn>
              <v-spacer></v-spacer>
              <v-btn text @click="addTermActive = false">Cancel</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-toolbar>
      <v-data-table :items="terms" :headers="headers" :server-items-length="termCount" :options.sync="options" @update:options="loadTerms">
        <template v-slot:item.startDate="{ item }">{{ stringFormatDate(item.startDate).substr(0, 17) }}</template>
        <template v-slot:item.openDate="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" @click="changeDate(item)">{{ stringFormatDate(item.openDate).substr(0, 17) }}</v-btn>
            </template>
            <span>Click to change open date</span>
          </v-tooltip>
        </template>
        <template v-slot:item.sections="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" @click="() => { selectTerm(item); $router.push('/admin/sections') }">{{ item.sections }} Sections</v-btn>
            </template>
            <span>Click to go to the section manager for this term</span>
          </v-tooltip>
        </template>
      </v-data-table>
      <v-dialog v-model="datePickerModal" :return-value.sync="date" persistent width="290px">
        <v-date-picker v-model="date" scrollable>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="datePickerModal = false">
            Cancel
          </v-btn>
          <v-btn text color="primary" @click="saveDate">
            OK
          </v-btn>
        </v-date-picker>
      </v-dialog>
    </v-col>
  </v-row>
</template>
<script>
import { hasRole } from '../../helpers/has'
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import { stringFormatDate } from '../../helpers/formatters'
export default {
  components: {
    DatePicker: () => import('@/components/forms/inputs/DatePicker')
  },
  setup (props, { root, emit }) {
    const user = computed(() => root.$store.state.user)
    const isAdmin = computed(() => root.$store.getters.isAdmin)

    const infoModal = ref(false)
    const range = ref('future')
    const termCount = ref(0)
    const terms = ref([])
    const headers = ref([
      { text: 'Term Description', value: 'label' },
      { text: 'Code', value: 'term' },
      { text: 'Start Date', value: 'startDate' },
      { text: 'Open Date', value: 'openDate' },
      { text: 'Sections', value: 'sections' }
    ])

    onMounted(() => {
      if (!hasRole(user.value, 'Employee')) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the admin system.' })
        root.$router.push('/')
        return
      }
      if (!root.$store.getters.isAdmin) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the term manager.' })
        root.$router.push('/admin')
        return
      }
      loadTerms()
      let query = { $limit: 20, $sort: { startDate: -1 }, startDate: { $lte: new Date() } }
      root.$feathers.service('orientation/term').find({ query }).then(async ({ data }) => {
        copyTermOptions.value = data.map(({ term, label }) => { return { term, label } })
      })
    })

    const options = ref({
      page: 1,
      itemsPerPage: 10,
      sortBy: [ 'startDate' ],
      sortDesc: [ false ]
    })
    function loadTerms (newOptions) {
      if (newOptions) {
        for (let l in newOptions) {
          options.value[l] = newOptions[l]
        }
      }
      let query = { $limit: options.value.itemsPerPage, $skip: (options.page - 1) * options.value.itemsPerPage, $sort: {} }
      if (options.value.sortBy.length > 0) {
        for (let i = 0; i < options.value.sortBy.length; i++) {
          query.$sort[options.value.sortBy[i]] = options.value.sortDesc[i] ? -1 : 1
        }
      } else {
        query.$sort.startDate = 1
      }
      if (range.value === 'future') {
        let date = new Date()
        date.setDate(date.getDate() - 7)
        query.startDate = { $gt: date }
      } else if (range.value === 'past') {
        let date = new Date()
        query.startDate = { $lt: date }
      }

      root.$feathers.service('orientation/term').find({ query }).then(async ({ total, data }) => {
        termCount.value = total
        terms.value = data
        for (let i = 0; i < terms.value.length; i++) {
          let { term } = terms.value[i]
          let { total } = await root.$feathers.service('orientation/section').find({ query: { term, $limit: 0 } })
          let temp = JSON.parse(JSON.stringify(terms.value[i]))
          temp.sections = total
          terms.value.splice(i, 1, temp)
        }
      })
    }

    watch(range, () => { loadTerms() })

    const datePickerModal = ref(false)
    const date = ref('')
    const changeDateTerm = ref('')

    function changeDate (item) {
      changeDateTerm.value = item.term
      let temp = new Date(item.openDate)
      date.value = temp.toISOString().substr(0, 10)
      datePickerModal.value = true
    }

    function saveDate () {
      datePickerModal.value = false
      for (let i = 0; i < terms.value.length; i++) {
        if (changeDateTerm.value === terms.value[i].term) {
          let item = JSON.parse(JSON.stringify(terms.value[i]))
          let temp = new Date(date.value)
          temp.setHours(24)
          item.openDate = temp
          root.$feathers.service('orientation/term').patch(item._id, { openDate: temp })
          terms.value.splice(i, 1, item)
        }
      }
    }

    const addTermActive = ref(false)
    const termSearch = ref('')
    const termSearchResults = ref([])
    watch(termSearch, () => {
      let query = { $sort: { start: 1 }, start: { $gt: new Date() } }
      if (termSearch.value !== '' && termSearch.value != null) {
        query.label = { $regex: termSearch.value, $options: 'i' }
      }
      root.$feathers.service('system/term').find({ query }).then(({ data }) => {
        termSearchResults.value = data
      })
    })
    const formModel = ref(null)
    const form = ref(null)
    const required = ref([v => !!v || 'Required'])
    const termType = ref('')
    const familyTerm = ref('')
    const newTerm = ref(null)
    const newTermStart = ref(null)
    const newTermOpen = ref(null)
    watch(newTerm, () => {
      if (newTerm.value != null && typeof (newTerm.value) === 'object') {
        let dt = new Date(newTerm.value.start)
        dt.setDate(dt.getDate() - 90)
        newTermOpen.value = dt.toISOString().substr(0, 10)
      }
    })
    const copyTerm = ref(null)
    const copyTermOptions = ref([])
    const addingTerm = ref(false)
    const addTermProgress = ref(0)
    async function addTerm () {
      if (!form.value.validate()) {
        return false
      }
      addingTerm.value = true
      let term = {
        category: termType.value,
        term: termType.value === 'Family' ? familyTerm.value : newTerm.value.term,
        label: termType.value === 'Family' ? familyTerm.value : newTerm.value.label,
        startDate: termType.value === 'Family' ? newTermStart.value : newTerm.value.start,
        openDate: newTermOpen.value
      }
      const sectServ = root.$feathers.service('orientation/section')
      const blockServ = root.$feathers.service('orientation/block')
      try {
        await root.$feathers.service('orientation/term').create(term)
      } catch (e) {
        addingTerm.value = false
        return
      }
      if (copyTerm.value != null) {
        // Load the sections for the selected term
        const { data } = await sectServ.find({ query: { term: copyTerm.value, active: true, $limit: 50 } })
        const progressInterval = 100 / (data.length + 1)
        addTermProgress.value = progressInterval
        // For each section:
        for (let i = 0; i < data.length; i++) {
          // Create a copy of the section, updating the term
          let section = JSON.parse(JSON.stringify(data[i]))
          section.term = term.term
          delete section._id
          // Create a new entry for this section
          const { _id: newSectionId } = await sectServ.create(section)
          section._id = newSectionId

          // Go through each slide and each column of each slide to find all of the blocks
          for (let j = 0; j < section.slides.length; j++) {
            // Load each block from the primary column; create a new copy of it and update the primary column entry with the ID of the new block
            if ('primary' in section.slides[j] && section.slides[j].primary.length > 0) {
              for (let k = 0; k < section.slides[j].primary.length; k++) {
                let block = await blockServ.get(section.slides[j].primary[k].id)
                delete block._id
                block.section = newSectionId
                if (block.type === 'Carousel') {
                  // Separate logic for carousels because of some being too large
                  const { items } = block
                  delete block.items
                  let newBlock = await blockServ.create(block)
                  section.slides[j].primary[k].id = newBlock._id
                  for (const item of items) {
                    await blockServ.patch(newBlock._id, { $push: { items: item } })
                  }
                } else {
                  let newBlock = await blockServ.create(block)
                  section.slides[j].primary[k].id = newBlock._id
                }
              }
            }
            // Load each block from the secondary column; create a new copy of it and update the secondary column entry with the ID of the new block
            if ('secondary' in section.slides[j] && section.slides[j].secondary.length > 0) {
              for (let k = 0; k < section.slides[j].secondary.length; k++) {
                let block = await blockServ.get(section.slides[j].secondary[k].id)
                delete block._id
                block.section = newSectionId
                let newBlock = await blockServ.create(block)
                section.slides[j].secondary[k].id = newBlock._id
              }
            }
          }
          await sectServ.update(newSectionId, section)
          addTermProgress.value += progressInterval
        }
        emit('termCreated', term.term)
        terms.value.push(term)
        selectTerm(term)
        newTerm.value = null
        newTermOpen.value = null
        copyTerm.value = null
        addTermActive.value = false
        addTermProgress.value = 100
      } else {
        addTermProgress.value = 100
        terms.value.push(term)
        selectTerm(term)
        newTerm.value = null
        newTermOpen.value = null
        copyTerm.value = null
        addTermActive.value = false
      }
      addingTerm.value = false
    }

    function selectTerm ({ term, label }) {
      root.$store.commit('admin/setTerm', term)
      root.$store.commit('admin/setTermLabel', label)
      emit('changeTerm', term)
    }

    return {
      user,
      isAdmin,
      infoModal,
      range,
      termCount,
      terms,
      headers,
      options,
      loadTerms,
      datePickerModal,
      date,
      changeDate,
      saveDate,
      selectTerm,
      addTermActive,
      termSearch,
      termSearchResults,
      termType,
      formModel,
      form,
      required,
      familyTerm,
      newTerm,
      newTermStart,
      newTermOpen,
      copyTerm,
      copyTermOptions,
      addingTerm,
      addTermProgress,
      addTerm,
      stringFormatDate
    }
  }
}
</script>
